import HBIcon from "@/components/HBIcon";
import HBInput from "@/components/HBInput";
import HBMessage from "@/components/HBMessage";
import HBModal, { type HBModalProps } from "@/components/HBModal";
import HBConfirm from "@/components/HBModal/confirm";
import ModalWrap from "@/components/HBModal/ModalWrap";
import Image from "@/components/Image";
import { UserList } from "@/components/User";
import {
  COLLAB_ROLE,
  COLLAB_ROLES,
  MEMBER_INVITE_STATUS,
} from "@/constants/board";
import useAppStore from "@/context/store";
import {
  changeCollabRole,
  exitBoardMember,
  getBoardMembers,
  getInviteCode,
  inviteBoardMember,
  removeBoardMember,
} from "@/services";
import useReq from "@/utils/useReq";
import { useDebounceFn, useMemoizedFn } from "ahooks";
import { Button, Switch, Tooltip } from "antd";
import classNames from "classnames";
import { useState } from "react";
import { InviteAction } from "..";
import styles from "./index.module.less";

function BoardMemberMngView({
  board,
  onUpdateBoard,
  onRemoveMember,
  onExitBoard,
  onCancel,
}: {
  board: IBoardInfo;
  onUpdateBoard?: (board: IBoardInfo) => void;
  onRemoveMember?: (user: IUser) => void;
  onExitBoard?: () => void;
  onCancel?: () => void;
}) {
  const currentUser = useAppStore((s) => s.user);
  const isOwner = board.user_id === currentUser?.user_id;
  const [searchKeyword, setSearchKeyword] = useState("");

  // 邀请状态
  const [inviting, setInviting] = useState<boolean>(
    isOwner && !(board.extra?.collab_members?.split(",")?.length > 0)
  );

  const { data, mutate: mutateMembersData } = useReq(async () => {
    // 没有协作成员
    if (!board.extra?.collab_members)
      return {
        data: [],
        total: 0,
      };

    // 获取所有协作成员
    const res = await getBoardMembers({ board_id: board.board_id });
    return res;
  });

  const { data: members, total } = data || { data: [], total: 0 };

  const mutateMembers = useMemoizedFn((fn) => {
    mutateMembersData((prev) => {
      return {
        data: fn(prev.data),
        total: prev.total,
      };
    });
  });

  const { data: inviteLink } = useReq(async () => {
    if (!isOwner) return "";

    const res = await getInviteCode({ board_id: board.board_id });

    if (res.err) return "";

    return `${location.origin}/boards/${board.board_id}?invite_code=${res.invitation_code}&share_source=copy`;
  });

  const { run: handleInvite } = useDebounceFn(async (user: IUser) => {
    const res = await inviteBoardMember({
      board_id: board.board_id,
      user_id: user.user_id,
    });

    if (res.err) {
      return;
    }

    mutateMembers((prev) => [
      ...prev,
      { ...user, status: MEMBER_INVITE_STATUS.INVITING },
    ]);

    HBMessage.success("已发送邀请，请等待对方确认");
  });

  const handleRemove = useMemoizedFn(async (user: IUser) => {
    const res = await removeBoardMember({
      board_id: board.board_id,
      user_id: user.user_id,
    });

    if (res.err) {
      return;
    }

    mutateMembers((prev) => prev.filter((m) => m.user_id !== user.user_id));

    onRemoveMember?.(user);
  });

  const handleExit = useMemoizedFn(async () => {
    const res = await exitBoardMember({
      board_id: board.board_id,
    });

    if (res.err) {
      return;
    }

    mutateMembers((prev) =>
      prev.filter((m) => m.user_id !== currentUser.user_id)
    );

    onExitBoard?.();

    onCancel?.();

    HBMessage.success("已退出画板");
  });

  const actionRender = useMemoizedFn((user: IUser & { is_member: boolean }) => {
    const status = members?.find((m) => m.user_id === user.user_id)?.status;

    // 非创建者，不能移除其他成员
    if (!isOwner && user.user_id !== currentUser.user_id) {
      return null;
    }

    // 画板创建者不能邀请自己，且不能退出画板
    if (isOwner && user.user_id === currentUser.user_id) {
      return null;
    }

    if (user.is_member) {
      return (
        <Button shape="round" disabled>
          已加入
        </Button>
      );
    }

    return (
      <InviteAction
        user={user}
        status={status}
        onClick={async () => {
          if (status === MEMBER_INVITE_STATUS.NORMAL) {
            HBConfirm.open({
              title:
                user.user_id === currentUser.user_id
                  ? "是否退出画板？"
                  : "是否移除成员？",
              content:
                user.user_id === currentUser.user_id
                  ? `退出后将无法再共同采集灵感`
                  : `移除后将无法再共同采集灵感`,
              okText: "确定",
              okType: "primary",
              onOk: () => {
                if (user.user_id === currentUser.user_id) {
                  handleExit();
                } else {
                  handleRemove(user);
                }
              },
            });
          } else {
            handleInvite(user);
          }
        }}
      />
    );
  });

  const [collabRole, setCollabRole] = useState<COLLAB_ROLE>(
    board?.extra?.collab
  );

  const hasMembers = members?.length > 0;

  const onChnageCollabRole = useMemoizedFn(async (checked) => {
    const res = await changeCollabRole({
      board_id: board.board_id,
      role: checked ? COLLAB_ROLES.EDITOR : COLLAB_ROLES.COLLECTOR,
    });
    if (res.err) return;
    setCollabRole(res.role);
    onUpdateBoard({
      ...board,
      extra: { ...board.extra, collab: res.role },
    });
  });

  // 邀请其他成员面板
  if (inviting) {
    return (
      <div data-gd-expose="dialog_expose" data-dialog-title="成员邀请弹窗">
        <div className={styles.header}>
          <div className={styles.label}>
            邀请协作成员
            <Image
              src={"https://grocery-cdn.huaban.com/file/free.svg"}
              width={30}
              height={16}
              alt="限免"
              style={{ marginLeft: 4 }}
            />
          </div>
          <div className={styles.info}>
            <div className={styles.inviteLink}>{inviteLink || "获取中..."}</div>
            <Button
              type="primary"
              shape="round"
              onClick={() => {
                navigator.clipboard.writeText(
                  `快来和我一起采集灵感吧，要用电脑浏览器打开哦～ ${inviteLink}`
                );
                HBMessage.success("复制成功");
              }}
              style={{ height: "48px" }}
              data-gd-click="button_click"
              data-button-name="复制链接"
            >
              复制链接
            </Button>
          </div>
          <div className={styles.search}>
            <HBInput
              placeholder="输入用户名搜索协作成员"
              value={searchKeyword}
              onChange={(e) => setSearchKeyword(e.target.value)}
              noWrapper
            />
          </div>
        </div>

        <div className={styles.searchList}>
          <UserList
            board={board}
            selecteds={[]}
            searchKeyword={searchKeyword}
            maxHeight={400}
            renderAction={actionRender}
          />
        </div>
      </div>
    );
  }

  const canInvite = total < 20;

  // 默认
  return (
    <div
      className={styles.container}
      data-gd-expose="dialog_expose"
      data-dialog-title="成员管理弹窗"
    >
      {isOwner && hasMembers && (
        <div className={styles.header}>
          <div className={styles.label}>成员权限</div>
          <div className={styles.switch}>
            <Switch
              checked={collabRole === COLLAB_ROLES.EDITOR}
              onChange={onChnageCollabRole}
            />
            允许编辑采集描述及删除采集
          </div>
          <div className={styles.desc}>
            关闭该权限，成员仍然可以采集至该画板，但是无法编辑他人采集及删除采集
          </div>
        </div>
      )}

      <div
        className={styles.list}
        style={{
          marginLeft: 12,
          marginBottom: isOwner ? 0 : 24,
        }}
      >
        <div className={styles.memberCount}>
          画板成员 {members?.length || 0}/20
          <Tooltip placement="bottom" title="如需扩容请联系客服">
            <HBIcon type="ic_info1" />
          </Tooltip>
        </div>
        <UserList
          invitedUsers={members}
          selecteds={[]}
          maxHeight={400}
          renderAction={actionRender}
          board={board}
        />
      </div>
      {isOwner && (
        <Tooltip
          {...(canInvite
            ? {
                visible: false,
              }
            : {})}
          placement="top"
          title="成员数量已达到上限，如需扩容请联系客服"
        >
          <div
            className={classNames(styles.footer, {
              [styles.disabled]: !canInvite,
            })}
            data-gd-click="button_click"
            data-button-name="邀请协作成员"
            onClick={() => (canInvite ? setInviting(true) : null)}
          >
            <Button
              shape="circle"
              style={{ width: 40, height: 40, fontSize: 20 }}
            >
              <HBIcon type="hb_add_member" />
            </Button>
            邀请协作成员
          </div>
        </Tooltip>
      )}
    </div>
  );
}

/**
 * 画板成员管理弹窗
 * @returns
 */
export const BoardMemberMngDialog = ModalWrap(function BoardMemberMngDialog({
  board,
  onUpdateBoard,
  onRemoveMember,
  onExitBoard,
  ...props
}: HBModalProps & {
  board: IBoardInfo;
  onRemoveMember: (user: IUser) => void;
  onExitBoard: () => void;
  onUpdateBoard: (board: IBoardInfo) => void;
}) {
  return (
    <HBModal
      {...props}
      title="画板成员"
      footer={null}
      className={styles.modal}
      width={480}
    >
      <BoardMemberMngView
        board={board}
        onUpdateBoard={onUpdateBoard}
        onRemoveMember={onRemoveMember}
        onExitBoard={onExitBoard}
        onCancel={props.onCancel}
      />
    </HBModal>
  );
});
